import { Component, type ErrorInfo, type ReactNode } from 'react'
import { isStaleChunkError, reloadOnceForStaleChunk } from '@/lib/staleChunkReload'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // A route chunk from a previous build. Reloading fixes it, and the raw
    // "Failed to fetch dynamically imported module" stack helps nobody.
    if (reloadOnceForStaleChunk(error)) return
    this.setState({ errorInfo })
    console.error('[ErrorBoundary]', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      // A reload is already in flight — do not flash a stack trace at the user.
      if (isStaleChunkError(this.state.error)) {
        return (
          <div className="flex items-center justify-center min-h-[50vh] p-8">
            <p className="text-sm text-muted-foreground">Updating to the latest version…</p>
          </div>
        )
      }
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="flex items-center justify-center min-h-[50vh] p-8">
          <div className="max-w-lg w-full space-y-4">
            <h2 className="text-lg font-semibold text-destructive">Something went wrong</h2>
            <pre className="text-xs bg-muted rounded p-3 overflow-auto max-h-60 whitespace-pre-wrap">
              {this.state.error?.message}
              {'\n\n'}
              {this.state.errorInfo?.componentStack}
            </pre>
            <button
              type="button"
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90"
              onClick={() => {
                this.setState({ hasError: false, error: null, errorInfo: null })
                window.location.reload()
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
